import React, { useEffect, useMemo, useState } from "react";
import api from "../../../services/api";
import ReportsFilters from "../../../components/reports/ReportsFilters";
import PaymentBreakdownChart from "../../../components/reports/PaymentBreakdownChart";
import DownloadCsvButton from "../../../components/reports/DownloadCsvButton";
import { buildCsvFromSections, downloadCsv } from "../../../utils/csvExport";
import {
  buildDefaultFilters,
  buildReportParams,
  currency,
  ReportFilters,
} from "./filterUtils";

interface PaymentsReportResponse {
  stripeRevenue: number;
  cashRevenue: number;
  totalTransactions: number;
  paymentBreakdown: Array<{
    method: string;
    orders: number;
    amount: number;
  }>;
  dailyPayments: Array<{
    date: string;
    stripe: number;
    cash: number;
  }>;
}

const initialData: PaymentsReportResponse = {
  stripeRevenue: 0,
  cashRevenue: 0,
  totalTransactions: 0,
  paymentBreakdown: [],
  dailyPayments: [],
};

const PaymentsReport: React.FC = () => {
  const [filters, setFilters] = useState<ReportFilters>(buildDefaultFilters());
  const [data, setData] = useState<PaymentsReportResponse>(initialData);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        const res = await api.get<PaymentsReportResponse>("/reports/payments", {
          params: buildReportParams(filters),
        });
        setData(res.data);
      } catch (error) {
        console.error("Failed to fetch payments report", error);
        setData(initialData);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [filters]);

  const summaryCards = useMemo(
    () => [
      { label: "Stripe Revenue", value: currency(data.stripeRevenue) },
      { label: "Cash Revenue", value: currency(data.cashRevenue) },
      { label: "Total Transactions", value: data.totalTransactions.toString() },
    ],
    [data],
  );
  const hasPaymentBreakdownData = useMemo(
    () =>
      data.paymentBreakdown.some(
        (entry) => entry.orders > 0 || entry.amount > 0,
      ),
    [data.paymentBreakdown],
  );

  const handleDownloadCsv = () => {
    const csv = buildCsvFromSections([
      {
        title: "Summary",
        headers: ["Metric", "Value"],
        rows: summaryCards.map((card) => [card.label, card.value]),
      },
      {
        title: "Payment Breakdown",
        headers: ["Payment Method", "Orders", "Amount"],
        rows: data.paymentBreakdown.map((row) => [
          row.method.toUpperCase(),
          row.orders,
          currency(row.amount),
        ]),
      },
      {
        title: "Daily Payment Report",
        headers: ["Date", "Stripe Revenue", "Cash Revenue"],
        rows: data.dailyPayments.map((row) => [
          row.date,
          currency(row.stripe),
          currency(row.cash),
        ]),
      },
    ]);
    downloadCsv(
      `payments-report-${filters.startDate}-to-${filters.endDate}.csv`,
      csv,
    );
  };

  return (
    <div className="mx-auto h-screen max-w-7xl overflow-y-auto no-scrollbar p-4 pt-0 lg:p-8">
      <div className="sticky top-0 z-10 flex items-center justify-between bg-white/90 py-6 backdrop-blur-sm">
        <h1 className="text-2xl font-bold text-gray-800">Payments Report</h1>
        <DownloadCsvButton onClick={handleDownloadCsv} disabled={loading} />
      </div>

      <ReportsFilters value={filters} onChange={setFilters} />
      {loading ? (
        <div className="flex min-h-[400px] items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {summaryCards.map((card) => (
              <div key={card.label} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <p className="mb-1 text-xs font-semibold text-blue-600">{card.label}</p>
                <p className="text-2xl font-bold text-gray-800">{card.value}</p>
              </div>
            ))}
          </div>

          <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <h2 className="mb-3 text-lg font-semibold text-gray-800">Payment Breakdown</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse">
                  <thead className="bg-gray-50 text-xs text-gray-700 sm:text-sm">
                    <tr className="border-b border-gray-200">
                      <th className="p-3 text-left font-semibold">Payment Method</th>
                      <th className="p-3 text-center font-semibold">Orders</th>
                      <th className="p-3 text-center font-semibold">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.paymentBreakdown.map((row) => (
                      <tr key={row.method} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="p-3 text-sm text-gray-700">{row.method.toUpperCase()}</td>
                        <td className="p-3 text-center text-sm text-gray-700">{row.orders}</td>
                        <td className="p-3 text-center text-sm text-gray-700">{currency(row.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <h2 className="mb-3 text-lg font-semibold text-gray-800">Payment Distribution</h2>
              {hasPaymentBreakdownData ? (
                <PaymentBreakdownChart data={data.paymentBreakdown} />
              ) : (
                <div className="flex h-[280px] items-center justify-center text-sm text-gray-500">
                  No data available for selected filters.
                </div>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <h2 className="mb-3 text-lg font-semibold text-gray-800">Daily Payment Report</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse">
                <thead className="bg-gray-50 text-xs text-gray-700 sm:text-sm">
                  <tr className="border-b border-gray-200">
                    <th className="p-3 text-left font-semibold">Date</th>
                    <th className="p-3 text-center font-semibold">Stripe Revenue</th>
                    <th className="p-3 text-center font-semibold">Cash Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {data.dailyPayments.map((row) => (
                    <tr key={row.date} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="p-3 text-sm text-gray-700">{row.date}</td>
                      <td className="p-3 text-center text-sm text-gray-700">{currency(row.stripe)}</td>
                      <td className="p-3 text-center text-sm text-gray-700">{currency(row.cash)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentsReport;
